import { useMemo } from 'react';
import { IProcountorInvoicePreviewResponse } from '../ZentuvoInvoicingApi';
import { getSearchText, IVisibleGroup, normalizeOrderRowId, SortKey, sortRows } from '../procountorInvoiceHelpers';

type UseVisibleInvoiceGroupsArgs = {
    preview: IProcountorInvoicePreviewResponse | null;
    searchText: string;
    statusFilter: string;
    validityFilter: string;
    sortKey: SortKey;
    sortDirection: 'asc' | 'desc';
    selectedRowIds: number[];
};

type UseVisibleInvoiceGroupsResult = {
    filteredGroups: IVisibleGroup[];
    firstInvalidGroupId: string;
    visibleRowIds: number[];
    selectedAmountTotal: number;
    visibleAmountTotal: number;
    hasSelection: boolean;
    hasRows: boolean;
    allVisibleSelected: boolean;
    someVisibleSelected: boolean;
    selectedAllValid: boolean;
};

export function useVisibleInvoiceGroups({
    preview,
    searchText,
    statusFilter,
    validityFilter,
    sortKey,
    sortDirection,
    selectedRowIds
}: UseVisibleInvoiceGroupsArgs): UseVisibleInvoiceGroupsResult {
    return useMemo(() => {
        const filteredGroups: IVisibleGroup[] = [];
        const normalizedSearchText = searchText.trim().toLowerCase();
        if (preview) {
            (preview.Groups || []).forEach((group) => {
                const visibleRows = sortRows(
                    (group.Rows || []).filter((row) => {
                        if (statusFilter !== 'all' && String(row.InvoiceStatusValue) !== statusFilter) {
                            return false;
                        }

                        if (validityFilter === 'valid' && !row.HasValidProcountorId) {
                            return false;
                        }

                        if (validityFilter === 'invalid' && row.HasValidProcountorId) {
                            return false;
                        }

                        if (normalizedSearchText && getSearchText(group, row).indexOf(normalizedSearchText) === -1) {
                            return false;
                        }

                        return true;
                    }),
                    sortKey,
                    sortDirection
                );

                if (visibleRows.length > 0) {
                    filteredGroups.push({ group, rows: visibleRows });
                }
            });
        }

        filteredGroups.sort((leftGroup, rightGroup) => {
            const leftInvalid = leftGroup.group.HasValidProcountorId ? 1 : 0;
            const rightInvalid = rightGroup.group.HasValidProcountorId ? 1 : 0;
            return leftInvalid - rightInvalid;
        });

        const firstInvalidGroup = filteredGroups.find((visibleGroup) => !visibleGroup.group.HasValidProcountorId);
        const firstInvalidGroupId = firstInvalidGroup ? firstInvalidGroup.group.GroupId : '';

        const visibleRowIds: number[] = [];
        let selectedAmountTotal = 0;
        let visibleAmountTotal = 0;
        filteredGroups.forEach((visibleGroup) => {
            visibleGroup.rows.forEach((row) => {
                const normalizedOrderRowId = normalizeOrderRowId(row.OrderRowId);
                visibleRowIds.push(normalizedOrderRowId);
                visibleAmountTotal += row.TotalAmount;
                if (selectedRowIds.indexOf(normalizedOrderRowId) >= 0) {
                    selectedAmountTotal += row.TotalAmount;
                }
            });
        });

        const hasSelection = selectedRowIds.length > 0;
        const hasRows = preview !== null && (preview.Groups || []).length > 0;
        const allVisibleSelected = visibleRowIds.length > 0 && visibleRowIds.every((rowId) => selectedRowIds.indexOf(rowId) >= 0);
        const someVisibleSelected = visibleRowIds.some((rowId) => selectedRowIds.indexOf(rowId) >= 0);

        const selectedRowsValidityMap: { [key: number]: boolean } = {};
        if (preview) {
            (preview.Groups || []).forEach((group) => {
                (group.Rows || []).forEach((row) => {
                    selectedRowsValidityMap[normalizeOrderRowId(row.OrderRowId)] = !!row.HasValidProcountorId;
                });
            });
        }

        const selectedAllValid = selectedRowIds.length > 0 && selectedRowIds.every((id) => selectedRowsValidityMap[id] === true);

        return {
            filteredGroups,
            firstInvalidGroupId,
            visibleRowIds,
            selectedAmountTotal,
            visibleAmountTotal,
            hasSelection,
            hasRows,
            allVisibleSelected,
            someVisibleSelected,
            selectedAllValid
        };
    }, [preview, searchText, statusFilter, validityFilter, sortKey, sortDirection, selectedRowIds]);
}
